// TODO: Consider splitting into separate stores:
// - contractStore (contracts, currentContract, fetchContracts, createContract, updateContract, deleteContract, fetchContractById)
// - postStore (posts, _postCache, fetchPosts, fetchAllPosts, fetchPostsByDateRange, createPost, updatePost, deletePost, bulkUpdatePosts, markStoryDone, invalidatePostCache)
// - syncStore (isRefreshing, refreshStats, isPolling, refreshFromNotion, fullRefreshFromNotion, checkSyncStatus)

import { create } from 'zustand';
import { InstagramContract, InstagramPost } from '../types';
import api from '../utils/api';
import { handleApiError, handleApiSuccess, handleApiWarning } from '../utils/errorHandler';
import { logger } from '../utils/logger';
import { useUndoStore } from './undoStore';
import { useConfirmClientStore } from './confirmClientStore';

const MAX_CACHE_ENTRIES = 50;

/** Supprime les entrées les plus anciennes du cache si la taille dépasse MAX_CACHE_ENTRIES */
function pruneCache(cache: Map<string, { posts: InstagramPost[]; fetchedAt: number }>): void {
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) cache.delete(oldestKey);
    else break;
  }
}

// ===== Persistance localStorage (re-hydratation instantanée au boot) =====
const LS_KEY = 'nudge-instagram-store-v1';
const LS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7j

interface PersistedState {
  contracts: InstagramContract[];
  posts: InstagramPost[];
  savedAt: number;
}

function loadPersisted(): { contracts: InstagramContract[]; posts: InstagramPost[] } {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { contracts: [], posts: [] };
    const data = JSON.parse(raw) as PersistedState;
    if (!data || typeof data !== 'object') return { contracts: [], posts: [] };
    if (Date.now() - (data.savedAt || 0) > LS_MAX_AGE_MS) return { contracts: [], posts: [] };
    return {
      contracts: Array.isArray(data.contracts) ? data.contracts : [],
      posts: Array.isArray(data.posts) ? data.posts : [],
    };
  } catch { return { contracts: [], posts: [] }; }
}

let lsWriteTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePersist(get: () => { contracts: InstagramContract[]; posts: InstagramPost[] }): void {
  if (lsWriteTimer) return;
  lsWriteTimer = setTimeout(() => {
    lsWriteTimer = null;
    try {
      const { contracts, posts } = get();
      const payload: PersistedState = { contracts, posts, savedAt: Date.now() };
      localStorage.setItem(LS_KEY, JSON.stringify(payload));
    } catch (e) {
      // QuotaExceeded ou JSON cycle → on abandonne silencieusement
    }
  }, 1000);
}

interface NotionSyncStats {
  postsCreated?: number;
  postsUpdated?: number;
  postsSkipped?: number;
  mediasUploaded?: number;
  clientsUpdated?: number;
  contractsUpdated?: number;
}

interface InstagramStore {
  contracts: InstagramContract[];
  posts: InstagramPost[];
  currentContract: InstagramContract | null;
  isLoading: boolean;
  loadError: boolean; // true si le chargement initial a échoué (réseau, timeout…)
  isRefreshing: boolean;
  refreshStats: NotionSyncStats | null;
  isPolling: boolean; // ✅ Prevent concurrent polling
  // Cache per contract: contractId -> { posts, fetchedAt }
  _postCache: Map<string, { posts: InstagramPost[]; fetchedAt: number }>;
  _fetchVersion: number; // Compteur pour éviter les race conditions sur fetchPosts
  _fetchInFlight: Map<string, Promise<void>>; // Déduplication des requêtes en cours

  // Actions
  fetchContracts: () => Promise<void>;
  fetchContractById: (id: string) => Promise<void>;
  createContract: (contractData: Partial<InstagramContract>) => Promise<InstagramContract>;
  updateContract: (id: string, contractData: Partial<InstagramContract>) => Promise<InstagramContract>;
  deleteContract: (id: string) => Promise<void>;
  fetchPosts: (contractId?: string, status?: string) => Promise<void>;
  fetchPostsByDateRange: (startDate: Date, endDate: Date, contractId?: string) => Promise<void>;
  fetchAllPosts: () => Promise<void>;
  createPost: (postData: Partial<InstagramPost>) => Promise<InstagramPost>;
  updatePost: (id: string, postData: Partial<InstagramPost>) => Promise<InstagramPost>;
  deletePost: (id: string) => Promise<void>;
  refreshFromNotion: () => Promise<void>;
  fullRefreshFromNotion: () => Promise<void>;
  checkSyncStatus: () => Promise<void>;
  bulkUpdatePosts: (postIds: string[], updates: { status?: string; scheduledDate?: string; contractId?: string }) => Promise<void>;
  markStoryDone: (postId: string) => Promise<any>;
  setCurrentContract: (contract: InstagramContract | null) => void;
  invalidatePostCache: (contractId?: string) => void;
  // Corbeille
  fetchTrash: (contractId: string) => Promise<InstagramPost[]>;
  restorePost: (id: string) => Promise<void>;
  permanentDeletePost: (id: string) => Promise<void>;
  emptyTrash: (contractId: string) => Promise<number>;
}

// Hydratation synchrone depuis localStorage au boot — re-rendu instantané
const __hydrated = loadPersisted();

export const useInstagramStore = create<InstagramStore>((set, get) => ({
  contracts: __hydrated.contracts,
  posts: __hydrated.posts,
  currentContract: null,
  isLoading: false,
  loadError: false,
  isRefreshing: false,
  refreshStats: null,
  isPolling: false, // ✅ Prevent concurrent polling
  _postCache: new Map(),
  _fetchVersion: 0,
  _fetchInFlight: new Map(),

  fetchContracts: async () => {
    set({ isLoading: true, loadError: false });

    try {
      const response = await api.get('/instagram/contracts');
      const contracts = response.data;

      set({ contracts, isLoading: false, loadError: false });
      logger.log('✅ Contrats Instagram chargés:', contracts.length);
    } catch (error: any) {
      handleApiError(error, 'le chargement des contrats Instagram');
      set({ isLoading: false, loadError: true });
      throw error;
    }
  },

  fetchContractById: async (id: string) => {
    set({ isLoading: true });

    try {
      const response = await api.get(`/instagram/contracts/${id}`);
      const contract = response.data;

      set({ currentContract: contract, isLoading: false });
      logger.log('✅ Contrat Instagram récupéré:', contract.id);

      // Mettre à jour aussi dans la liste des contrats
      set(state => ({
        contracts: state.contracts.map(c => c.id === contract.id ? contract : c)
      }));
    } catch (error: any) {
      handleApiError(error, 'la récupération du contrat Instagram');
      set({ isLoading: false });
      throw error;
    }
  },

  createContract: async (contractData: Partial<InstagramContract>) => {
    set({ isLoading: true });

    try {
      const response = await api.post('/instagram/contracts', contractData);
      const newContract = response.data.contract;

      set(state => ({
        contracts: [...state.contracts, newContract],
        isLoading: false
      }));

      handleApiSuccess('Contrat Instagram créé avec succès');
      logger.log('✅ Nouveau contrat Instagram créé:', newContract.id);
      return newContract;
    } catch (error: any) {
      handleApiError(error, 'la création du contrat Instagram');
      set({ isLoading: false });
      throw error;
    }
  },

  updateContract: async (id: string, contractData: Partial<InstagramContract>) => {
    set({ isLoading: true });

    try {
      const response = await api.put(`/instagram/contracts/${id}`, contractData);
      const updatedContract = response.data.contract;

      set(state => ({
        contracts: state.contracts.map(contract =>
          contract.id === id ? updatedContract : contract
        ),
        currentContract: state.currentContract?.id === id ? updatedContract : state.currentContract,
        isLoading: false
      }));

      handleApiSuccess('Contrat Instagram mis à jour avec succès');
      logger.log('✅ Contrat Instagram mis à jour:', updatedContract.id);
      return updatedContract;
    } catch (error: any) {
      handleApiError(error, 'la mise à jour du contrat Instagram');
      set({ isLoading: false });
      throw error;
    }
  },

  deleteContract: async (id: string) => {
    set({ isLoading: true });

    try {
      await api.delete(`/instagram/contracts/${id}`);

      set(state => ({
        contracts: state.contracts.filter(contract => contract.id !== id),
        posts: state.posts.filter(post => post.contractId !== id),
        currentContract: state.currentContract?.id === id ? null : state.currentContract,
        isLoading: false
      }));

      handleApiSuccess('Contrat Instagram supprimé avec succès');
      logger.log('✅ Contrat Instagram supprimé:', id);
    } catch (error: any) {
      handleApiError(error, 'la suppression du contrat Instagram');
      set({ isLoading: false });
      throw error;
    }
  },

  fetchPosts: async (contractId?: string, status?: string) => {
    const CACHE_TTL = 30_000; // 30s cache validity
    const cacheKey = contractId || '__all__';
    const inFlightKey = `${cacheKey}:${status || ''}`;

    // Déduplication : si une requête identique est en cours, attendre celle-ci
    const existing = get()._fetchInFlight.get(inFlightKey);
    if (existing) {
      logger.log(`⏳ fetchPosts dédupliqué (${inFlightKey})`);
      return existing;
    }

    // Check cache first - serve stale data instantly, refresh in background
    if (contractId && !status) {
      const cached = get()._postCache.get(cacheKey);
      if (cached) {
        const age = Date.now() - cached.fetchedAt;
        // Serve cached data immediately — FUSIONNER pour préserver les autres contrats
        set(state => ({
          posts: [
            ...state.posts.filter(p => p.contractId !== contractId),
            ...cached.posts
          ]
        }));

        // If cache is fresh enough, skip the API call entirely
        if (age < CACHE_TTL) {
          logger.log(`✅ Posts from cache (${Math.round(age / 1000)}s old):`, cached.posts.length);
          return;
        }
        // Otherwise, refresh in background (no isLoading = no spinner)
      }
    }

    // Loading spinner uniquement si on n'a AUCUNE donnée à afficher
    // (ni en cache, ni déjà dans le store)
    const hasCachedData = contractId && get()._postCache.has(cacheKey);
    const hasStoreData = get().posts.length > 0;
    if (!hasCachedData && !hasStoreData) {
      set({ isLoading: true });
    }

    // Race condition protection : incrémenter le compteur de version
    const currentVersion = get()._fetchVersion + 1;
    set({ _fetchVersion: currentVersion });

    // Wrap dans une promise stockée pour déduplication
    const fetchPromise = (async () => {
    try {
      const params: any = {};
      if (contractId) params.contractId = contractId;
      if (status) params.status = status;

      const response = await api.get('/instagram/posts', { params });
      const posts = response.data;

      // Si un fetch plus récent a été lancé, ignorer ce résultat
      if (get()._fetchVersion !== currentVersion) {
        logger.log('⏭️ fetchPosts ignoré (version obsolète)');
        return;
      }

      // Si on filtre par contrat, FUSIONNER au lieu de remplacer
      // pour ne pas écraser les posts des autres contrats
      if (contractId) {
        set(state => ({
          posts: [
            ...state.posts.filter(p => p.contractId !== contractId),
            ...posts
          ],
          isLoading: false
        }));
      } else {
        set({ posts, isLoading: false });
      }

      // Store in cache
      if (contractId && !status) {
        const cache = new Map(get()._postCache);
        cache.set(cacheKey, { posts, fetchedAt: Date.now() });
        pruneCache(cache);
        set({ _postCache: cache });
      }

      logger.log('✅ Posts Instagram récupérés:', posts.length);

      // Si on a un contrat courant et qu'on filtre par contractId, mettre à jour le contrat avec ses posts
      if (contractId) {
        const state = get();
        if (state.currentContract && state.currentContract.id === contractId) {
          set({
            currentContract: {
              ...state.currentContract,
              posts
            }
          });
        }

        // Mettre à jour aussi dans la liste des contrats
        set(state => ({
          contracts: state.contracts.map(c =>
            c.id === contractId ? { ...c, posts } : c
          )
        }));
      }
    } catch (error: any) {
      handleApiError(error, 'le chargement des publications Instagram');
      set({ isLoading: false });
      throw error;
    } finally {
      // Cleanup la requête in-flight
      const inFlightMap = new Map(get()._fetchInFlight);
      inFlightMap.delete(inFlightKey);
      set({ _fetchInFlight: inFlightMap });
    }
    })();

    // Enregistrer la promise pour la déduplication
    const newInFlight = new Map(get()._fetchInFlight);
    newInFlight.set(inFlightKey, fetchPromise);
    set({ _fetchInFlight: newInFlight });

    return fetchPromise;
  },

  fetchPostsByDateRange: async (startDate: Date, endDate: Date, contractId?: string) => {
    const CACHE_TTL = 30_000;
    const cacheKey = `range_${startDate.toISOString().slice(0, 10)}_${endDate.toISOString().slice(0, 10)}_${contractId || 'all'}`;

    // Serve cached data instantly
    const cached = get()._postCache.get(cacheKey);
    if (cached) {
      const age = Date.now() - cached.fetchedAt;
      set({ posts: cached.posts });
      if (age < CACHE_TTL) {
        logger.log(`✅ Posts date range from cache (${Math.round(age / 1000)}s old):`, cached.posts.length);
        return;
      }
    }

    if (!cached && get().posts.length === 0) {
      set({ isLoading: true });
    }

    try {
      const params: any = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };
      if (contractId) params.contractId = contractId;

      const response = await api.get('/instagram/posts', { params });
      const posts = response.data;

      set({ posts, isLoading: false });

      // Store in cache
      const cache = new Map(get()._postCache);
      cache.set(cacheKey, { posts, fetchedAt: Date.now() });
      pruneCache(cache);
      set({ _postCache: cache });

      logger.log('✅ Posts Instagram chargés pour la plage de dates:', posts.length, 'posts');
    } catch (error: any) {
      handleApiError(error, 'le chargement des publications par plage de dates');
      set({ isLoading: false });
      throw error;
    }
  },

  fetchAllPosts: async () => {
    const CACHE_TTL = 30_000;
    const cacheKey = '__all__';

    // Servir le cache immédiatement si disponible (pas de loading = pas de freeze)
    const cached = get()._postCache.get(cacheKey);
    if (cached) {
      const age = Date.now() - cached.fetchedAt;
      set({ posts: cached.posts });

      if (age < CACHE_TTL) {
        logger.log(`✅ Tous les posts from cache (${Math.round(age / 1000)}s old):`, cached.posts.length);
        return;
      }
      // Cache périmé → refresh en background (sans isLoading)
    }

    // Loading spinner uniquement si aucune donnée en cache
    if (!cached) {
      set({ isLoading: true });
    }

    try {
      const response = await api.get('/instagram/posts');
      const posts = response.data;

      set({ posts, isLoading: false });

      // Stocker en cache
      const cache = new Map(get()._postCache);
      cache.set(cacheKey, { posts, fetchedAt: Date.now() });
      pruneCache(cache);
      set({ _postCache: cache });

      logger.log('✅ Tous les posts Instagram chargés:', posts.length);
    } catch (error: any) {
      handleApiError(error, 'le chargement de toutes les publications Instagram');
      set({ isLoading: false });
      throw error;
    }
  },

  createPost: async (postData: Partial<InstagramPost>) => {
    set({ isLoading: true });

    try {
      const response = await api.post('/instagram/posts', postData);
      const newPost = response.data.post;

      // Ajouter le post à la liste
      set(state => ({
        posts: [...state.posts, newPost],
        isLoading: false
      }));

      // Mettre à jour le contrat correspondant avec le nouveau post
      const contractId = newPost.contractId;
      const state = get();

      set({
        contracts: state.contracts.map(contract => {
          if (contract.id === contractId) {
            return {
              ...contract,
              posts: [...(contract.posts || []), newPost]
            };
          }
          return contract;
        }),
        currentContract: state.currentContract?.id === contractId && state.currentContract
          ? {
              ...state.currentContract,
              posts: [...(state.currentContract.posts || []), newPost]
            }
          : state.currentContract
      });

      // Invalidate cache for this contract
      get().invalidatePostCache(newPost.contractId);

      handleApiSuccess('Publication Instagram créée avec succès');
      logger.log('✅ Nouveau post Instagram créé:', newPost.id);
      return newPost;
    } catch (error: any) {
      handleApiError(error, 'la création de la publication Instagram');
      set({ isLoading: false });
      throw error;
    }
  },

  updatePost: async (id: string, postData: Partial<InstagramPost>) => {
    // Garde "À valider" : quand on passe un post en validation, prévenir que le CLIENT va le voir
    // sur son espace (popup throttlé 30 min/client). Vaut pour tous les agents (chokepoint unique).
    if (postData.status === 'to_validate') {
      const existingForGuard = get().posts.find(p => p.id === id);
      if (existingForGuard && existingForGuard.status !== 'to_validate') {
        const contractForGuard = get().contracts.find(c => c.id === existingForGuard.contractId);
        const clientName = (contractForGuard as any)?.client?.name
          || (existingForGuard as any)?.contract?.client?.name || '';
        const ok = await useConfirmClientStore.getState().request({
          clientId: existingForGuard.contractId || '',
          clientName,
        });
        if (!ok) return existingForGuard; // annulé → aucun changement, aucune erreur
      }
    }

    set({ isLoading: true });

    try {
      // Sauvegarder l'état précédent pour l'undo
      const previousPost = get().posts.find(p => p.id === id);

      const response = await api.put(`/instagram/posts/${id}`, postData);
      const updatedPost = response.data.post;

      // Sticker de story : le serveur bascule le post en « à poster à la main » et annule sa
      // programmation Buffer (l'API Instagram ne pose pas de stickers). On le dit tout de suite.
      const sticker = response.data.stickerSwitch;
      const bSync = response.data.bufferSync;
      if (sticker?.switched) {
        // Si Buffer a refusé la déprogrammation, la story partirait SANS le sticker → alerte franche.
        if (bSync?.action === 'unscheduled' && bSync?.success === false) {
          handleApiError(
            { response: { data: { message: `Passé en « À poster à la main », mais la déprogrammation Buffer a échoué (${bSync.message}). Vérifie dans Buffer que la story n'est plus programmée, sinon elle partira sans le sticker.` } } },
            'la déprogrammation Buffer',
          );
        } else {
          handleApiWarning(`Passé en « À poster à la main » (déprogrammé de Buffer) : ${sticker.reason}`, 'sticker de story');
        }
      } else if (sticker?.cleared) {
        handleApiWarning(sticker.reason, 'sticker de story');
      }

      // Mettre à jour dans la liste des posts
      set(state => ({
        posts: state.posts.map(post =>
          post.id === id ? updatedPost : post
        ),
        isLoading: false
      }));

      // Mettre à jour dans le contrat correspondant
      const contractId = updatedPost.contractId;
      const state = get();

      set({
        contracts: state.contracts.map(contract => {
          if (contract.id === contractId && contract.posts) {
            return {
              ...contract,
              posts: contract.posts.map(p => p.id === id ? updatedPost : p)
            };
          }
          return contract;
        }),
        currentContract: state.currentContract?.id === contractId && state.currentContract?.posts
          ? {
              ...state.currentContract,
              posts: state.currentContract.posts.map(p => p.id === id ? updatedPost : p)
            }
          : state.currentContract
      });

      // Invalidate cache for this contract
      get().invalidatePostCache(updatedPost.contractId);

      // Message intelligent selon l'action Buffer
      const lateSync = response.data.lateSync;
      if (lateSync?.action === 'unscheduled' && lateSync.success) {
        handleApiSuccess('Post mis à jour — programmation annulée sur Buffer');
      } else if (lateSync?.action === 'unscheduled' && !lateSync.success) {
        handleApiWarning(`Post mis à jour — échec annulation Buffer : ${lateSync.message}`);
      } else if (lateSync?.action === 'updated' && lateSync.success) {
        handleApiSuccess('Post mis à jour — programmation synchronisée sur Buffer');
      } else if (lateSync?.action === 'updated' && !lateSync.success) {
        handleApiWarning(`Post mis à jour localement — échec sync Buffer : ${lateSync.message}`);
      } else {
        handleApiSuccess('Publication mise à jour');
      }
      logger.log('✅ Post Instagram mis à jour:', updatedPost.id);

      // Undo : restaurer l'état précédent
      if (previousPost) {
        const postName = previousPost.name || 'Post';
        const revertData: Partial<InstagramPost> = {};
        for (const key of Object.keys(postData) as (keyof InstagramPost)[]) {
          (revertData as any)[key] = (previousPost as any)[key];
        }
        useUndoStore.getState().pushUndo(`"${postName}" modifié`, async () => {
          await api.put(`/instagram/posts/${id}`, revertData);
          get().invalidatePostCache(contractId);
          await get().fetchAllPosts();
          handleApiSuccess('Modification annulée');
        });
      }

      return updatedPost;
    } catch (error: any) {
      // Intercept 409 CONFIRM_EXTERNAL_PUBLISH
      if (error.response?.status === 409 && error.response?.data?.code === 'CONFIRM_EXTERNAL_PUBLISH') {
        set({ isLoading: false });
        // Throw a structured error for the component to handle
        const customError = new Error('CONFIRM_EXTERNAL_PUBLISH');
        (customError as any).needsExternalConfirm = true;
        (customError as any).retryFn = async (confirmedExternalPublish: boolean) => {
          return api.put(`/instagram/posts/${id}`, {
            ...postData,
            confirmedExternalPublish
          }).then(res => res.data.post);
        };
        throw customError;
      }
      handleApiError(error, 'la mise à jour de la publication Instagram');
      set({ isLoading: false });
      throw error;
    }
  },

  deletePost: async (id: string) => {
    set({ isLoading: true });

    try {
      // Get the post before deleting to know its contractId
      const state = get();
      const postToDelete = state.posts.find(p => p.id === id);
      const contractId = postToDelete?.contractId;

      await api.delete(`/instagram/posts/${id}`);

      // Supprimer de la liste des posts
      set(state => ({
        posts: state.posts.filter(post => post.id !== id),
        isLoading: false
      }));

      // Supprimer du contrat correspondant
      if (contractId) {
        set(state => ({
          contracts: state.contracts.map(contract => {
            if (contract.id === contractId && contract.posts) {
              return {
                ...contract,
                posts: contract.posts.filter(p => p.id !== id)
              };
            }
            return contract;
          }),
          currentContract: state.currentContract?.id === contractId && state.currentContract?.posts
            ? {
                ...state.currentContract,
                posts: state.currentContract.posts.filter(p => p.id !== id)
              }
            : state.currentContract
        }));
      }

      // Invalidate cache for this contract
      if (contractId) get().invalidatePostCache(contractId);

      handleApiSuccess('Publication mise en corbeille');
      logger.log('🗑️ Post mis en corbeille:', id);

      // Undo : restaurer le post
      const postName = postToDelete?.name || 'Post';
      useUndoStore.getState().pushUndo(`"${postName}" supprimé`, async () => {
        await get().restorePost(id);
      });
    } catch (error: any) {
      handleApiError(error, 'la mise en corbeille de la publication');
      set({ isLoading: false });
      throw error;
    }
  },

  fetchTrash: async (contractId: string) => {
    try {
      const response = await api.get(`/instagram/posts/trash?contractId=${contractId}`);
      return response.data;
    } catch (error: any) {
      handleApiError(error, 'la récupération de la corbeille');
      return [];
    }
  },

  restorePost: async (id: string) => {
    try {
      await api.post(`/instagram/posts/${id}/restore`);

      // Invalidate cache pour recharger les posts
      get().invalidatePostCache();
      await get().fetchAllPosts();

      handleApiSuccess('Publication restaurée');
    } catch (error: any) {
      handleApiError(error, 'la restauration de la publication');
      throw error;
    }
  },

  permanentDeletePost: async (id: string) => {
    try {
      await api.delete(`/instagram/posts/${id}/permanent`);
      handleApiSuccess('Publication supprimée définitivement');
    } catch (error: any) {
      handleApiError(error, 'la suppression définitive');
      throw error;
    }
  },

  emptyTrash: async (contractId: string) => {
    try {
      const response = await api.delete(`/instagram/posts/trash/empty?contractId=${contractId}`);
      const count = response.data.count || 0;
      handleApiSuccess(`${count} publication(s) supprimée(s) définitivement`);
      return count;
    } catch (error: any) {
      handleApiError(error, 'le vidage de la corbeille');
      throw error;
    }
  },

  refreshFromNotion: async () => {
    set({ isRefreshing: true, refreshStats: null });

    try {
      logger.log('🔄 Lancement du refresh Notion...');

      // Lancer le refresh (async)
      api.post('/notion/refresh').catch(err => logger.error('Erreur refresh:', err));

      // Démarrer le polling
      get().checkSyncStatus();

    } catch (error: any) {
      handleApiError(error, 'la synchronisation depuis Notion');
      set({ isRefreshing: false, refreshStats: null });
      throw error;
    }
  },

  fullRefreshFromNotion: async () => {
    set({ isRefreshing: true, refreshStats: null });

    try {
      logger.log('🚀 Lancement du FULL REFRESH Notion (TOUS les posts)...');

      // Lancer le full refresh (async)
      api.post('/notion/full-refresh').catch(err => logger.error('Erreur full refresh:', err));

      // Démarrer le polling
      get().checkSyncStatus();

    } catch (error: any) {
      handleApiError(error, 'la synchronisation complète depuis Notion');
      set({ isRefreshing: false, refreshStats: null });
      throw error;
    }
  },

  checkSyncStatus: async () => {
    // ✅ Prevent concurrent polling
    const state = get();
    if (state.isPolling) {
      logger.log('⏭️ Polling déjà en cours, skip');
      return;
    }

    set({ isPolling: true });

    try {
      const statusResponse = await api.get('/notion/status');
      const { isRunning, status, stats } = statusResponse.data;

      if (!isRunning && status === 'completed' && stats) {
        // Sync terminé
        const refreshStats: NotionSyncStats = {
          postsCreated: stats?.posts?.created || 0,
          postsUpdated: stats?.posts?.updated || 0,
          postsSkipped: stats?.posts?.skipped || 0,
          mediasUploaded: stats?.medias?.uploaded || 0,
          clientsUpdated: stats?.clients?.updated || 0,
          contractsUpdated: stats?.contracts?.updated || 0
        };

        set({ refreshStats, isRefreshing: false, isPolling: false });
        handleApiSuccess('Synchronisation Notion terminée avec succès');

        // Invalidate all cached posts then reload
        get().invalidatePostCache();
        await get().fetchContracts();
        await get().fetchAllPosts();

        // Garder les stats visibles pendant 10 secondes
        setTimeout(() => {
          set({ refreshStats: null });
        }, 10000);
      } else if (isRunning) {
        // Sync en cours - continuer le polling
        set({ isRefreshing: true, isPolling: false });
        // ✅ Recheck dans 2 secondes (au lieu de 1s pour réduire la charge)
        setTimeout(() => get().checkSyncStatus(), 2000);
      } else if (status === 'error') {
        set({ isRefreshing: false, isPolling: false });
        handleApiError(new Error('Erreur lors de la synchronisation'), 'la synchronisation depuis Notion');
      } else {
        // Status idle - STOP polling
        set({ isRefreshing: false, isPolling: false });
        logger.log('✅ Status idle, arrêt du polling');
      }
    } catch (error: any) {
      logger.error('Erreur check status:', error);
      set({ isRefreshing: false, isPolling: false });
    }
  },

  bulkUpdatePosts: async (postIds: string[], updates: { status?: string; scheduledDate?: string; contractId?: string }) => {
    try {
      // Sauvegarder l'état précédent pour l'undo
      const state = get();
      const previousStates = postIds.map(id => {
        const post = state.posts.find(p => p.id === id);
        if (!post) return null;
        const prev: any = {};
        if (updates.status) prev.status = post.status;
        if (updates.scheduledDate) prev.scheduledDate = post.scheduledDate;
        if (updates.contractId) prev.contractId = post.contractId;
        return { id, prev };
      }).filter(Boolean) as { id: string; prev: any }[];

      await api.put('/instagram/posts/bulk-update', { postIds, updates });

      // Invalider les caches des contrats impactés
      const affectedContractIds = new Set<string>();
      state.posts.forEach(p => {
        if (postIds.includes(p.id)) affectedContractIds.add(p.contractId);
      });
      if (updates.contractId) affectedContractIds.add(updates.contractId);
      affectedContractIds.forEach(id => get().invalidatePostCache(id));

      // Recharger les posts et contrats
      await get().fetchAllPosts();
      await get().fetchContracts();

      const count = postIds.length;
      handleApiSuccess(`${count} publication${count > 1 ? 's' : ''} mise${count > 1 ? 's' : ''} à jour`);

      // Undo : restaurer les états précédents
      if (previousStates.length > 0) {
        useUndoStore.getState().pushUndo(`${count} post${count > 1 ? 's' : ''} modifié${count > 1 ? 's' : ''}`, async () => {
          for (const { id, prev } of previousStates) {
            await api.put(`/instagram/posts/${id}`, prev);
          }
          get().invalidatePostCache();
          await get().fetchAllPosts();
          await get().fetchContracts();
          handleApiSuccess('Modifications annulées');
        });
      }
    } catch (error: any) {
      handleApiError(error, 'la modification en lot des publications');
      throw error;
    }
  },

  markStoryDone: async (postId: string) => {
    try {
      const response = await api.put(`/instagram/posts/${postId}/story-done`);
      // Update the post in state
      set(state => ({
        posts: state.posts.map(p => p.id === postId ? { ...p, status: 'published' } : p)
      }));
      handleApiSuccess('Story marquee comme faite');
      return response.data;
    } catch (error: any) {
      handleApiError(error, 'le marquage de la story comme faite');
    }
  },

  setCurrentContract: (contract: InstagramContract | null) => {
    set({ currentContract: contract });
  },

  invalidatePostCache: (contractId?: string) => {
    if (contractId) {
      const cache = new Map(get()._postCache);
      cache.delete(contractId);
      // Also invalidate range caches that might contain this contract
      Array.from(cache.keys()).forEach(key => {
        if (key.includes(contractId) || key.startsWith('range_')) {
          cache.delete(key);
        }
      });
      set({ _postCache: cache });
    } else {
      set({ _postCache: new Map() });
    }
  }
}));

// Persistance localStorage : sauvegarde debouncée à chaque changement de posts/contracts
let __lastPosts: InstagramPost[] = useInstagramStore.getState().posts;
let __lastContracts: InstagramContract[] = useInstagramStore.getState().contracts;
useInstagramStore.subscribe((state) => {
  if (state.posts !== __lastPosts || state.contracts !== __lastContracts) {
    __lastPosts = state.posts;
    __lastContracts = state.contracts;
    schedulePersist(() => ({ posts: state.posts, contracts: state.contracts }));
  }
});
